import { productPath } from '@eprintsmart/shared';
import { auth } from '../firebase';

/**
 * Telling search engines a page changed, the moment it changes.
 *
 * The storefront's /api/indexnow does the actual submitting — api.indexnow.org
 * sends no CORS headers, so a browser cannot post to it directly, and the key
 * has no business being in an admin bundle either. This is the thin caller.
 *
 * GOOGLE DOES NOT PARTICIPATE in IndexNow. This reaches Bing (and so Copilot),
 * Yandex, Naver, Seznam, Yep and Amazon. Google still finds changes through the
 * sitemap on its own schedule.
 */

const STOREFRONT_URL =
  (import.meta.env.VITE_STOREFRONT_URL as string | undefined) ?? 'https://eprintsmart.com';

export interface IndexNowOutcome {
  submitted: number;
  status: number;
  ok: boolean;
  message: string;
  /** Failed for a reason that resolves itself — wait rather than change anything. */
  retryable?: boolean;
}

/**
 * Local development must never announce anything.
 *
 * Editing a product against the emulator would otherwise ping the real search
 * engines with real production URLs — and the emulator's tokens do not verify
 * against production anyway, so it would only ever be noise or a false alarm.
 */
const OFFLINE = import.meta.env.VITE_USE_EMULATORS === 'true';

async function post(body: { paths?: string[]; all?: true }): Promise<IndexNowOutcome> {
  if (OFFLINE) {
    return { submitted: 0, status: 0, ok: true, message: 'Skipped: running against emulators.' };
  }

  const user = auth.currentUser;
  if (!user) throw new Error('Sign in first.');

  const response = await fetch(`${STOREFRONT_URL}/api/indexnow`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${await user.getIdToken()}`,
    },
    body: JSON.stringify(body),
  });

  const payload = (await response.json().catch(() => null)) as
    | (IndexNowOutcome & { error?: string })
    | null;

  if (!response.ok) {
    throw new Error(payload?.error ?? payload?.message ?? `Submission failed (${response.status}).`);
  }
  return payload as IndexNowOutcome;
}

/** Submit every indexable URL. For a first run, or after a bulk migration. */
export function submitAllUrls(): Promise<IndexNowOutcome> {
  return post({ all: true });
}

export function submitPaths(paths: string[]): Promise<IndexNowOutcome> {
  return post({ paths });
}

/**
 * Fire-and-forget notification that a product changed.
 *
 * NEVER REJECTS, and is never awaited by its callers. A search-engine ping is
 * the least important thing happening when somebody saves a product, and an
 * editor that reports "save failed" because a third party was briefly
 * unreachable would be actively worse than one that never pinged at all. The
 * failure is logged where a developer can see it and nowhere else.
 */
export function notifyProductChanged(slug: string): void {
  void submitPaths([productPath(slug), '/pricing'])
    .catch((err) => console.warn('[indexnow] ping failed, the save itself was fine:', err));
}
